import { isNil } from 'lodash-es';
import type React from 'react';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { JsonLd } from 'react-schemaorg';
import type { WebSite } from 'schema-dts';
import { lazyReactNaiveRetry } from '../../../constants/Lazy';
import SearchProvider from '../../../providers/SearchProvider/SearchProvider';
import ToasterProvider from '../../../providers/ToasterProvider';
import UmamiProvider from '../../../providers/UmamiProvider/UmamiProvider';
import UserProvider from '../../../providers/UserProvider';
import { setAppConfig } from '../../../redux/ducks/ConfigDucks';
import { setClientId } from '../../../redux/ducks/StorageDucks';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import InfoModal from '../../modals/InfoModal';
import AppRoutes from '../AppRoutes/AppRoutes';
import CookieConsent from '../CookieConsent/CookieConsent';

const RegisterModal = lazyReactNaiveRetry(
  () => import('../../modals/RegisterModal'),
);
const ResetPasswordModal = lazyReactNaiveRetry(
  () => import('../../modals/ResetPasswordModal'),
);
const SearchModal = lazyReactNaiveRetry(
  () => import('../../modals/SearchModal'),
);
const LoginModal = lazyReactNaiveRetry(() => import('../../modals/LoginModal'));
const JoinModal = lazyReactNaiveRetry(() => import('../../modals/JoinModal'));
const ForgotModal = lazyReactNaiveRetry(
  () => import('../../modals/ForgotModal'),
);
const ActionModal = lazyReactNaiveRetry(
  () => import('../../modals/ActionModal'),
);
const PushProvider = lazyReactNaiveRetry(
  () => import('../../../providers/PushProvider/PushProvider'),
);

interface AppProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

const App: React.FC<AppProps> = ({ ssrFetchedData }) => {
  const dispatch = useDispatch();
  const { clientId } = useSelector((state: RootReducerType) => state.storage);
  const [umamiWebsiteUrl, setUmamiWesiteUrl] = useState<string | undefined>();
  const [umamiWebsiteId, setUmamiWesiteId] = useState<string | undefined>();
  const [onClient, setOnClient] = useState<boolean>(false);

  useEffect(() => {
    if (ssrFetchedData) {
      const appConfiguration = ssrFetchedData.appConfiguration;
      setUmamiWesiteId(appConfiguration.babylon.umami_website_id);
      setUmamiWesiteUrl(appConfiguration.babylon.umami_domain);

      dispatch(setAppConfig(appConfiguration));
    }
  }, [ssrFetchedData]);

  useEffect(() => {
    if (!clientId) {
      const generatedClientId = `${Math.random()
        .toString(36)
        .slice(2)}${Math.random().toString(36).slice(2)}${Math.random()
        .toString(36)
        .slice(2)}${Math.random().toString(36).slice(2)}`.substring(0, 40);
      dispatch(setClientId(generatedClientId));
    }
  }, [clientId]);

  useEffect(() => {
    setOnClient(true);
  }, []);

  const umamiEnabled = useMemo<boolean>(
    () => !(isNil(umamiWebsiteUrl) || isNil(umamiWebsiteId)),
    [umamiWebsiteUrl, umamiWebsiteId],
  );

  return (
    <UserProvider>
      <SearchProvider>
        <div className="relative min-w-full flex flex-col">
          <div className="min-w-full grow">
            <UmamiProvider
              url={umamiWebsiteUrl || ''}
              id={umamiWebsiteId || ''}
              enabled={umamiEnabled}
            />
            <ToasterProvider />
            <AppRoutes ssrFetchedData={ssrFetchedData} />
            {onClient && (
              <>
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <PushProvider />
                </Suspense>
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <ActionModal />
                </Suspense>
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <InfoModal />
                </Suspense>
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <LoginModal />
                </Suspense>
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <RegisterModal />
                </Suspense>
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <JoinModal />
                </Suspense>
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <ForgotModal />
                </Suspense>
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <SearchModal />
                </Suspense>
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <ResetPasswordModal />
                </Suspense>
              </>
            )}
          </div>
          <div className="fixed bottom-0 center-0 z-40 min-w-full">
            <CookieConsent />
          </div>
          <JsonLd<WebSite>
            item={{
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              '@id': `${!import.meta.env.PROD ? 'http' : 'https'}://${
                ssrFetchedData.domain
              }`,
              name: ssrFetchedData.appConfiguration?.service.name,
              alternateName: [ssrFetchedData.domain],
              url: `${!import.meta.env.PROD ? 'http' : 'https'}://${
                ssrFetchedData.domain
              }`,
            }}
          />
        </div>
      </SearchProvider>
    </UserProvider>
  );
};

export default App;
